import { useState, useEffect, FC, memo } from "react";
import "./App.css";
import axios from "axios";
import i18n from "i18next";
import { useTranslation, initReactI18next } from "react-i18next";
import translationJA from "./locales/ja.json";
import translationEN from "./locales/en.json";

i18n.use(initReactI18next) // passes i18n down to react-i18next
    .init({
        // the translations
        // (tip move them in a JSON file and import them,
        // or even better, manage them via a UI: https://react.i18next.com/guides/multiple-translation-files#manage-your-translations-with-a-management-gui)
        resources: {
            ja: { translation: translationJA },
            en: { translation: translationEN },
        },
        lng: "en", // if you're using a language detector, do not define the lng option
        fallbackLng: "en",

        interpolation: {
            escapeValue: false, // react already safes from xss => https://www.i18next.com/translation-function/interpolation#unescape
        },
    });

type Watch = {
    id: string;
    name: string;
    price_prefix?: string;
    price: number;
    price_suffix?: string;
    image_name?: string;
    image_large: string;
    image_thumb: string;
    alt: string;
    link: string;
    defaultBandIndex?: number;
};
type Band = {
    id: string;
    name: string;
    color: string;
    price_prefix?: string;
    price: number;
    price_suffix?: string;
    image_name?: string;
    image_large: string;
    image_thumb: string;
    alt: string;
    link: string;
};
type Props = {
    lang?: string | null;
};
export const App: FC<Props> = memo(({ lang }) => {
    const { t, i18n } = useTranslation();
    const [currentWatchClass] = useState<string>("ph_head current");
    const [currentBandClass] = useState<string>("ph_band current absolute");

    const initailWatchIndex = 0;
    const initailBandIndex = null;
    const initailDefaultBandIndex = 0;

    const [defaultBandList, setDefaultBandList] = useState<Band[]>([]);
    const [watchList, setWatchList] = useState<Watch[]>([]);
    const [bandList, setBandList] = useState<Band[]>([]);

    const [currentWatchIndex, setCurrentWatchIndex] =
        useState<number>(initailWatchIndex);
    const [currentBandIndex, setCurrentBandIndex] = useState<number | null>(
        initailBandIndex
    );
    const [defaultBandIndex, setDefaultBandIndex] = useState<number>(
        initailDefaultBandIndex
    );

    const [currentWatch, setCurrentWatch] = useState<Watch>();
    const [currentBand, setCurrentBand] = useState<Band>();

    const [downloadImage, setDownloadImage] = useState<string>("");

    const changeCurrentWatchIndex = (index: number) =>
        index === currentWatchIndex
            ? setCurrentWatchIndex(initailWatchIndex)
            : setCurrentWatchIndex(index);
    const changeCurrentBandIndex = (index: number) =>
        index === currentBandIndex
            ? setCurrentBandIndex(initailBandIndex)
            : setCurrentBandIndex(index);

    useEffect(() => {
        if (currentWatch && currentBand) {
            const watchName = currentWatch.image_name || currentWatch.name;
            const bandName = currentBand.image_name || currentBand.name;
            if (currentBandIndex != initailBandIndex) {
                setDownloadImage(
                    `assets/image/img_${watchName}_${bandName}.jpg`
                );
            } else {
                setDownloadImage(`assets/image/img_${watchName}_default.jpg`);
            }
        } else {
            setDownloadImage("");
        }
    }, [currentWatch, currentBand]);

    const reset = () => {
        setCurrentWatchIndex(initailWatchIndex);
        setCurrentBandIndex(initailBandIndex);
    };

    //言語設定
    useEffect(() => {
        if (lang) {
            i18n.changeLanguage(lang);
        }
    }, [lang, i18n]);

    //マスタデータ取得
    useEffect(() => {
        // console.log('master init trigger');
        if (lang) {
            axios.get(`assets/products.json`).then((res) => {
                setWatchList(res.data.watch);
                setBandList(res.data.band);
                setDefaultBandList(res.data.defaultBand);
            });
        }
    }, [lang]);

    //currentWatchIndex変更時の処理
    useEffect(() => {
        //初期ロード後に処理
        if (watchList.length > 0) {
            // CurrentWatchを変更
            const index = currentWatchIndex || initailWatchIndex;
            const watch = watchList[index];
            setCurrentWatch(watch);

            // DefaultBandを変更
            const defaultIndex =
                watch.defaultBandIndex || initailDefaultBandIndex;
            setDefaultBandIndex(defaultIndex);

            // currentBandIndexが未設定の場合、currentBandを変更
            if (currentBandIndex == initailBandIndex) {
                setCurrentBand(defaultBandList[defaultIndex]);
            }
        }
    }, [currentWatchIndex, watchList]);

    //currentBandIndex変更時の処理
    useEffect(() => {
        //初期ロード後に処理
        if (bandList.length > 0 && defaultBandList.length > 0) {
            currentBandIndex != initailBandIndex
                ? setCurrentBand(bandList[currentBandIndex])
                : setCurrentBand(defaultBandList[defaultBandIndex]);
        }
    }, [currentBandIndex, bandList, defaultBandList, defaultBandIndex]);

    return (
        <>
            <div className="preload">
                {watchList.map((watch) => (
                    <link
                        key={watch.id}
                        rel="preload"
                        href={watch.image_large}
                        as="image"
                    />
                ))}
                {bandList.map((band) => (
                    <link
                        key={band.id}
                        rel="preload"
                        href={band.image_large}
                        as="image"
                    />
                ))}
            </div>
            <div className="fix_area">
                <div className="inner">
                    <div className="ph">
                        <div className={currentWatchClass}>
                            {currentWatch && (
                                <img
                                    src={currentWatch.image_large}
                                    alt="currentWatch.alt"
                                />
                            )}
                        </div>
                        <div className={currentBandClass}>
                            {currentBand && (
                                <img
                                    src={currentBand.image_large}
                                    alt="currentBand.alt"
                                />
                            )}
                        </div>
                    </div>
                    <div className="text">
                        <div className="box_name cf">
                            <div>
                                <p className="h_name">
                                    {currentWatch && currentWatch.name}
                                </p>
                                <p className="b_name">
                                    {currentBand && currentBand.name}
                                </p>
                            </div>
                        </div>
                        <div className="btn">
                            <a href={downloadImage} target="_blank">
                                {t("preview-image")}
                            </a>
                        </div>
                    </div>
                </div>
            </div>
            <div className="select_area">
                <div className="inner">
                    <ul className="tab cf">
                        <li className="watch active">
                            <span>Watch</span>
                        </li>
                        <li className="band">
                            <span>Band</span>
                        </li>
                        <span className="box_reset">
                            <button type="button" onClick={reset}>
                                Reset
                            </button>
                        </span>
                    </ul>
                    <div className="tab_contents">
                        <ul className="show wrap_watch cf">
                            {watchList.map((watch, index) => (
                                <li
                                    key={watch.id}
                                    onClick={() =>
                                        changeCurrentWatchIndex(index)
                                    }
                                    className={
                                        index === currentWatchIndex
                                            ? "active"
                                            : ""
                                    }
                                >
                                    <div className="ph">
                                        <img
                                            src={watch.image_thumb}
                                            alt={watch.alt}
                                        />
                                    </div>
                                    <dl>
                                        <dt>{watch.name}</dt>
                                        <dd>
                                            {watch.price_prefix && (
                                                <span>
                                                    {watch.price_prefix}{" "}
                                                </span>
                                            )}
                                            {watch.price.toLocaleString()}
                                            {watch.price_suffix && (
                                                <span>
                                                    {watch.price_suffix}
                                                </span>
                                            )}
                                        </dd>
                                    </dl>
                                    {watch.link && (
                                        <div className="btn">
                                            <a
                                                href={watch.link}
                                                target="_blank"
                                            >
                                                {t("buy")}
                                            </a>
                                        </div>
                                    )}
                                </li>
                            ))}
                            <li>
                                <div className="footer_sp"></div>
                            </li>
                        </ul>
                        <ul className="wrap_band cf">
                            {bandList.map((band, index) => (
                                <li
                                    key={band.id}
                                    onClick={() =>
                                        changeCurrentBandIndex(index)
                                    }
                                    className={
                                        index === currentBandIndex
                                            ? "active"
                                            : ""
                                    }
                                >
                                    <div className="ph">
                                        <img
                                            src={band.image_thumb}
                                            alt={band.alt}
                                        />
                                    </div>
                                    <dl>
                                        <dt>{band.name}</dt>
                                        <dt>{band.color}</dt>
                                        <dd>
                                            {band.price_prefix && (
                                                <span>
                                                    {band.price_prefix}{" "}
                                                </span>
                                            )}
                                            {band.price.toLocaleString()}
                                            {band.price_suffix && (
                                                <span>{band.price_suffix}</span>
                                            )}
                                        </dd>
                                    </dl>
                                    {band.link && (
                                        <div className="btn">
                                            <a href={band.link} target="_blank">
                                                {t("buy")}
                                            </a>
                                        </div>
                                    )}
                                </li>
                            ))}
                            <li>
                                <div className="footer_sp"></div>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </>
    );
});
